import React, { useRef, ReactElement } from 'react'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { TableOfContents } from '../table_of_contents/TableOfContents'
import { Box } from '@mui/material'
import { createDndTocContext, useDndTocContext } from './context'
import { DndNavItem, WithParentId } from './types'
import { TOCProps } from '../table_of_contents/types'

interface DndTocProps<T extends DndNavItem<T>> extends TOCProps<T> {
  onItemDrop: (
    droppedItem: WithParentId<T>,
    nextItem: WithParentId<T>,
    parentId: string | undefined,
  ) => void
}
export function DndToc<T extends DndNavItem<T>>({
  onItemDrop,
  items,
  onItemSelect,
  ...props
}: DndTocProps<T>) {
  const DndTocContext = createDndTocContext<T>()
  const onItemSelectWrapped = (item: WithParentId<T>) => {
    onItemSelect(item as T)
  }
  const [dndItems, setDndItems] = React.useState<WithParentId<T>[]>([])

  React.useEffect(() => {
    setDndItems(processDndItems(items))
  }, [items])

  return (
    <DndProvider backend={HTML5Backend}>
      <DndTocContext.Provider value={{ onItemDrop }}>
        <TableOfContents
          {...props}
          items={dndItems}
          itemWrapper={DndTocWrapper}
          onItemSelect={onItemSelectWrapped}
        />
      </DndTocContext.Provider>
    </DndProvider>
  )
}

function processDndItems<T extends DndNavItem<T>>(
  items: T[],
): WithParentId<T>[] {
  const doItem = (item: T, parentId?: string): T & { parentId?: string } => {
    // @ts-ignore
    const children = item.children.map((c) => doItem(c, item.id))
    return {
      ...item,
      parentId,
      children,
    }
  }
  return items.map((item) => doItem(item))
}

// This wraps TOC items to make them drag/droppable
function DndTocWrapper<T extends WithParentId<DndNavItem<T>>>(
  component: ReactElement,
  item: T,
) {
  const { onItemDrop } = useDndTocContext<T>()
  const ref = useRef(null)
  const [{ isOver, mItem }, drop] = useDrop<
    T,
    void,
    { isOver: boolean; mItem: { id: string } }
  >({
    accept: item.type,
    drop(dragItem) {
      if (dragItem.id === item.id) {
        return
      }
      if (isOver) {
        onItemDrop(dragItem, item, item.parentId)
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
      mItem: monitor.getItem(),
    }),
  })
  const [{ isDragging }, drag] = useDrag(() => ({
    type: item.type,
    item,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  }))

  drag(drop(ref))

  return (
    <Box>
      {isOver && `---- ${mItem.id === item.id ? 'Cancel' : 'Insert Here'} ----`}
      <div ref={ref} style={{ opacity: isDragging ? 0 : 1 }}>
        {component}
      </div>
    </Box>
  )
}
